import { CourseContent } from "@/components/course/CourseContent";
import { CourseTree } from "@/components/course/tree/CourseTree";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useCallback, useEffect, useRef, useState } from "react";
import type { PanelImperativeHandle, PanelSize } from "react-resizable-panels";

const STORAGE_KEY = "sidebar-collapsed";
const MINI_THRESHOLD_PX = 180;
const ANIMATION_DURATION_MS = 300;

const getStorageValue = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStorageValue = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
};

const getInitialCollapsed = (): boolean =>
  getStorageValue(STORAGE_KEY) === "true";

export const DesktopCourseLayout = () => {
  const panelRef = useRef<PanelImperativeHandle>(null);
  const initialCollapsedValue = getInitialCollapsed();
  const initialCollapsed = useRef(initialCollapsedValue);
  const [isMiniSidebar, setIsMiniSidebar] = useState(initialCollapsedValue);
  const [isAnimating, setIsAnimating] = useState(false);

  // Restore collapsed state on mount (no animation)
  useEffect(() => {
    if (initialCollapsed.current) {
      panelRef.current?.collapse();
    }
  }, []);

  const handleResize = useCallback((panelSize: PanelSize) => {
    const mini = panelSize.inPixels <= MINI_THRESHOLD_PX;
    setIsMiniSidebar(mini);
    setStorageValue(STORAGE_KEY, String(mini));
  }, []);

  // Animate panel, then clear flag after transition finishes
  const animatePanel = useCallback((action: () => void) => {
    setIsAnimating(true);
    action();
    setTimeout(() => setIsAnimating(false), ANIMATION_DURATION_MS);
  }, []);

  const handleExpandSidebar = useCallback(() => {
    animatePanel(() => panelRef.current?.expand());
  }, [animatePanel]);

  // Only apply transition class during programmatic collapse/expand, not while dragging
  const panelTransition = isAnimating
    ? "transition-[flex-grow] duration-300 ease-in-out"
    : "";

  return (
    <div className="hidden h-full min-h-0 w-full md:flex">
      <ResizablePanelGroup orientation="horizontal" className="h-full w-full">
        <ResizablePanel
          className={panelTransition}
          maxSize="50%"
          defaultSize="25%"
          minSize="200px"
          collapsible
          collapsedSize="160px"
          panelRef={panelRef}
          onResize={handleResize}
        >
          <div className="h-full border-r bg-white flex flex-col">
            <div className="flex-1 overflow-hidden">
              <CourseTree
                showMinimizedSidebar={isMiniSidebar}
                onExpandSidebar={handleExpandSidebar}
              />
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle
          withHandle
          className="w-0 bg-border hover:bg-primary/20 transition-colors"
        />

        <ResizablePanel className={panelTransition} defaultSize="75%">
          <div className="h-full flex flex-col">
            <CourseContent />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};
